import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CONTACT } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/training", label: "Training" },
  { to: "/camps", label: "Camps" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <Link to="/" data-testid="nav-home-logo" aria-label="ARD Midsouth Performance home">
          <Logo size={110} />
        </Link>

        <ul className="hidden items-center gap-9 md:flex">
          {LINKS.map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`font-body text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:text-neon-green ${
                  pathname === l.to ? "text-neon-green" : "text-white/70"
                }`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-4 md:flex">
          <a
            href={CONTACT.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            aria-label="ARD on Instagram"
            className="flex h-11 w-11 items-center justify-center border-2 border-white/20 text-white transition-colors hover:border-neon-green hover:text-neon-green"
          >
            <Instagram size={20} strokeWidth={2.5} />
          </a>
          <button
            data-testid="nav-register-btn"
            onClick={() => navigate("/register")}
            className="group relative overflow-hidden border-2 border-neon-green px-6 py-3 font-heading text-xs uppercase tracking-widest text-neon-green transition-colors duration-300 hover:text-black"
          >
            <span className="relative z-10">Sign Up</span>
            <span className="absolute inset-0 -translate-x-full bg-neon-green transition-transform duration-300 group-hover:translate-x-0" />
          </button>
        </div>

        <div className="flex items-center gap-3 md:hidden">
          <a
            href={CONTACT.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram-mobile"
            aria-label="ARD on Instagram"
            className="text-white hover:text-neon-green"
          >
            <Instagram size={24} strokeWidth={2.5} />
          </a>
          <button
            className="text-white"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="border-t border-white/10 bg-black/95 backdrop-blur-xl md:hidden" data-testid="mobile-menu">
          <ul className="flex flex-col px-5 py-4">
            {LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`mobile-link-${l.label.toLowerCase()}`}
                  className="block border-b border-white/5 py-4 font-heading text-2xl uppercase tracking-wide text-white hover:text-neon-green"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/register"
                data-testid="mobile-register-btn"
                className="mt-4 block bg-neon-green py-4 text-center font-heading text-lg uppercase tracking-widest text-black"
              >
                Sign Up For Training
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
